/**
 * Pricing model for the /pricing calculator (owner-approved 2026-07-18):
 * standard rate 120 €/h at 7.5 h/day. Only the 6/12-month partnership tiers
 * discount, at a flat 90 €/h (−25%), and only at a partnership workload of
 * 75%+ of a week (4+ days, or 75% / 100% allocation; owner-approved
 * 2026-10-22); lighter workloads stay at the standard rate.
 *
 * Workload is one number, days per week. The days view offers 2–5 days; the
 * percentage view offers the allocations clients order (25/50/75/100% of a
 * 5-day week, so 1.25–5 days). Quarter days are exact binary fractions, so
 * the hour totals stay exact.
 */

export const STANDARD_RATE = 120;
export const HOURS_PER_DAY = 7.5;
export const FULL_WEEK_DAYS = 5;
/** 75% of a 5-day week. 4 and 5 days qualify; so do 75% and 100%. */
export const PARTNERSHIP_MIN_DAYS = 3.75;

const WEEKS_PER_MONTH = 52 / 12;

export type DurationId = "2w" | "1m" | "2m" | "3m" | "6m" | "12m";

export interface DurationOption {
  id: DurationId;
  /** Displayed quantity (2 weeks, 1 month, …). */
  amount: number;
  unit: "weeks" | "months";
  /** Duration in weeks as an exact fraction (52/12 weeks per month), so hour
   * totals can round on true midpoints instead of float noise. */
  weeksNumerator: number;
  weeksDenominator: number;
  weeks: number;
  /** Fractional discount applied to the hourly rate. */
  discount: number;
  /** Marks the long-commitment partnership tiers. */
  partnership: boolean;
}

export const DURATION_OPTIONS: DurationOption[] = [
  { id: "2w", amount: 2, unit: "weeks", weeksNumerator: 2, weeksDenominator: 1, weeks: 2, discount: 0, partnership: false },
  { id: "1m", amount: 1, unit: "months", weeksNumerator: 52, weeksDenominator: 12, weeks: WEEKS_PER_MONTH, discount: 0, partnership: false },
  { id: "2m", amount: 2, unit: "months", weeksNumerator: 104, weeksDenominator: 12, weeks: 2 * WEEKS_PER_MONTH, discount: 0, partnership: false },
  { id: "3m", amount: 3, unit: "months", weeksNumerator: 156, weeksDenominator: 12, weeks: 3 * WEEKS_PER_MONTH, discount: 0, partnership: false },
  { id: "6m", amount: 6, unit: "months", weeksNumerator: 312, weeksDenominator: 12, weeks: 6 * WEEKS_PER_MONTH, discount: 0.25, partnership: true },
  { id: "12m", amount: 12, unit: "months", weeksNumerator: 624, weeksDenominator: 12, weeks: 12 * WEEKS_PER_MONTH, discount: 0.25, partnership: true },
];

export const DAYS_PER_WEEK_OPTIONS = [2, 3, 4, 5] as const;

/** Allocations clients order, as a share of a full week. */
export const ALLOCATION_PERCENT_OPTIONS = [25, 50, 75, 100] as const;

export type WorkloadUnit = "days" | "percent";

export function percentToDays(percent: number): number {
  return (percent / 100) * FULL_WEEK_DAYS;
}

export function daysToPercent(days: number): number {
  return Math.round((days / FULL_WEEK_DAYS) * 100);
}

/** Short engagements (2 weeks / 1 month) require a partnership-level
 * workload (75%+); lighter paces are only offered from 2 months up. */
const SHORT_DURATIONS: DurationId[] = ["2w", "1m"];

/** Workload choices for a duration, as days per week, in the given view. */
export function getWorkloadOptions(
  durationId: DurationId,
  unit: WorkloadUnit = "days",
): number[] {
  const short = SHORT_DURATIONS.includes(durationId);
  const days: number[] =
    unit === "percent"
      ? ALLOCATION_PERCENT_OPTIONS.map(percentToDays)
      : [...DAYS_PER_WEEK_OPTIONS];
  return days.filter((value) => !short || value >= PARTNERSHIP_MIN_DAYS);
}

/** The option closest to a workload (ties go to the heavier one), used when
 * the view switches between days and percentages. */
export function nearestWorkload(days: number, options: number[]): number {
  return options.reduce((best, option) => {
    const diff = Math.abs(option - days);
    const bestDiff = Math.abs(best - days);
    return diff < bestDiff || (diff === bestDiff && option > best)
      ? option
      : best;
  }, options[0]);
}

export interface PricingBreakdown {
  totalHours: number;
  standardRate: number;
  /** Standard rate after the duration tier discount, rounded to whole euros. */
  effectiveRate: number;
  discount: number;
  /** Share of a full 5-day week, as a whole percentage. */
  allocationPercent: number;
  total: number;
  /** Saved versus the standard rate; 0 when no discount applies. */
  savings: number;
}

export function getDurationOption(id: DurationId): DurationOption {
  return DURATION_OPTIONS.find((option) => option.id === id) ?? DURATION_OPTIONS[0];
}

export function computePricing(
  durationId: DurationId,
  daysPerWeek: number,
): PricingBreakdown {
  const duration = getDurationOption(durationId);
  // Integer numerator (weeks×days×7.5 = ×15/2) so .5-hour midpoints round
  // consistently up instead of drifting on 52/12 float noise.
  const totalHours = Math.round(
    (duration.weeksNumerator * daysPerWeek * 15) /
      (2 * duration.weeksDenominator),
  );
  const discount =
    duration.discount > 0 && daysPerWeek >= PARTNERSHIP_MIN_DAYS
      ? duration.discount
      : 0;
  return breakdown(totalHours, discount, daysPerWeek);
}

function breakdown(
  totalHours: number,
  discount: number,
  daysPerWeek: number,
): PricingBreakdown {
  const effectiveRate = Math.round(STANDARD_RATE * (1 - discount));
  const total = Math.round(totalHours * effectiveRate);
  return {
    totalHours,
    standardRate: STANDARD_RATE,
    effectiveRate,
    discount,
    allocationPercent: daysToPercent(daysPerWeek),
    total,
    savings: totalHours * STANDARD_RATE - total,
  };
}

/**
 * Custom terms, for engagements the presets do not cover (3.5 months at 2.5
 * days a week): any value in range. The partnership rate follows the same
 * rule as the presets, 6+ months at a partnership workload.
 */
/** No upper limit on months: an engagement runs as long as the work needs. */
export const CUSTOM_MONTHS = { min: 0.5, max: Number.POSITIVE_INFINITY } as const;
export const CUSTOM_DAYS = { min: 1, max: FULL_WEEK_DAYS } as const;
export const PARTNERSHIP_MIN_MONTHS = 6;
const PARTNERSHIP_DISCOUNT = 0.25;

export interface CustomRange {
  min: number;
  max: number;
}

/**
 * Parses a typed custom value. Accepts a decimal comma ("3,5", as Finnish and
 * Swedish write it); returns null when empty, not a number or out of range.
 */
export function parseCustomValue(raw: string, range: CustomRange): number | null {
  const text = raw.trim().replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  return value < range.min || value > range.max ? null : value;
}

/** Person-days in a term: weeks × days per week (52/12 weeks a month). */
export function personDays(months: number, daysPerWeek: number): number {
  return (months * 52 * daysPerWeek) / 12;
}

/** Months in a preset duration (two weeks is half a month). */
export function durationMonths(option: DurationOption): number {
  return option.unit === "weeks" ? option.weeks / (52 / 12) : option.amount;
}

/** Pricing for a custom term in months at a custom number of days a week. */
export function computeCustomPricing(
  months: number,
  daysPerWeek: number,
): PricingBreakdown {
  // months × 52/12 weeks × days × 7.5 h, over one denominator so whole and
  // half inputs round on true midpoints.
  const totalHours = Math.round((months * 52 * daysPerWeek * 15) / (2 * 12));
  const discount =
    months >= PARTNERSHIP_MIN_MONTHS && daysPerWeek >= PARTNERSHIP_MIN_DAYS
      ? PARTNERSHIP_DISCOUNT
      : 0;
  return breakdown(totalHours, discount, daysPerWeek);
}

/**
 * Budget-first custom terms: the client gives a budget, the calculator gives
 * the term it buys. One budget fixes hours × rate, so with days a week given
 * the months follow, and with months given the days follow. Results round
 * down to one decimal so the price never exceeds the budget by rounding.
 * Where a budget can be read both ways (5 months at the standard rate or 6.7
 * months at the partnership rate), the longer partnership term wins: it is
 * the better deal for the client and a valid price for the same money.
 */
const HOURS_PER_MONTH_DAY = (52 / 12) * HOURS_PER_DAY;
const PARTNERSHIP_RATE = Math.round(STANDARD_RATE * (1 - PARTNERSHIP_DISCOUNT));

function floorTenth(value: number): number {
  // The epsilon keeps 4.1 from flooring to 4.0 on float noise (4.0999...).
  return Math.floor(value * 10 + 1e-9) / 10;
}

function qualifies(months: number, daysPerWeek: number): boolean {
  return months >= PARTNERSHIP_MIN_MONTHS && daysPerWeek >= PARTNERSHIP_MIN_DAYS;
}

/** Months a budget buys at a pace, before the 0.5-month minimum is applied. */
export function monthsForBudget(budget: number, daysPerWeek: number): number {
  const atPartnership = floorTenth(
    budget / PARTNERSHIP_RATE / (HOURS_PER_MONTH_DAY * daysPerWeek),
  );
  if (qualifies(atPartnership, daysPerWeek)) return atPartnership;
  return floorTenth(budget / STANDARD_RATE / (HOURS_PER_MONTH_DAY * daysPerWeek));
}

/** Days a week a budget buys over a term, before the 1-5 range is applied. */
export function daysForBudget(budget: number, months: number): number {
  const atPartnership = floorTenth(
    budget / PARTNERSHIP_RATE / (HOURS_PER_MONTH_DAY * months),
  );
  if (qualifies(months, atPartnership)) return atPartnership;
  return floorTenth(budget / STANDARD_RATE / (HOURS_PER_MONTH_DAY * months));
}

/** The least a custom term can cost: the minimum months at the minimum pace. */
export const MIN_CUSTOM_BUDGET = computeCustomPricing(
  CUSTOM_MONTHS.min,
  CUSTOM_DAYS.min,
).total;

/**
 * Parses a typed budget in whole euros: "40000", "40 000", "40,000 €" and
 * "40k" all read as 40000. Returns null for anything else.
 */
export function parseBudget(raw: string): number | null {
  const text = raw.trim().toLowerCase().replace(/[\s\u00a0\u202f€]/g, "");
  const thousands = /^(\d+(?:[.,]\d+)?)k$/.exec(text);
  if (thousands) return Math.round(Number(thousands[1].replace(",", ".")) * 1000);
  const digits = text.replace(/[.,]/g, "");
  return /^\d+$/.test(digits) && Number(digits) > 0 ? Number(digits) : null;
}

/** The preset tier whose "Perfect for" copy fits a custom term. */
export function tierForMonths(months: number): DurationId {
  if (months < 1) return "2w";
  if (months < 2) return "1m";
  if (months < 3) return "2m";
  if (months < 6) return "3m";
  if (months < 12) return "6m";
  return "12m";
}
